import { ShotData } from '../types';

/**
 * Comprehensive sample shot data for testing analytics
 * Includes various shot types, qualities, and realistic cricket parameters
 */
export const generateSampleShots = (): ShotData[] => {
  const shotTypes = [
    'straight-drive', 'cover-drive', 'square-cut', 'pull-shot', 
    'hook-shot', 'sweep-shot', 'flick', 'on-drive', 'off-drive',
    'late-cut', 'upper-cut', 'reverse-sweep'
  ];
  
  const qualities: Array<'excellent' | 'good' | 'fair' | 'poor'> = ['excellent', 'good', 'fair', 'poor'];
  
  const shots: ShotData[] = [];
  const baseDate = new Date();
  
  // Generate 100 sample shots over the past 30 days
  for (let i = 0; i < 100; i++) {
    const shotType = shotTypes[Math.floor(Math.random() * shotTypes.length)];
    const quality = qualities[Math.min(Math.floor(Math.random() * qualities.length * 1.2), qualities.length - 1)]; // Bias towards better quality
    
    // Calculate horizontal angle based on shot type
    let horizontalAngle = 0;
    if (shotType.includes('cover') || shotType.includes('off')) {
      horizontalAngle = -20 - Math.random() * 40; // -20 to -60 (off side)
    } else if (shotType.includes('pull') || shotType.includes('hook') || shotType.includes('flick') || shotType.includes('on')) {
      horizontalAngle = 20 + Math.random() * 40; // 20 to 60 (leg side)
    } else if (shotType.includes('square') || shotType.includes('cut')) {
      horizontalAngle = Math.random() > 0.5 ? -70 - Math.random() * 20 : 70 + Math.random() * 20; // Square of wicket
    } else if (shotType.includes('sweep')) {
      horizontalAngle = 45 + Math.random() * 30; // Fine leg to square leg
    } else {
      horizontalAngle = -10 + Math.random() * 20; // Straight
    }
    
    // Quality affects distance and other parameters
    const qualityMultiplier = quality === 'excellent' ? 1.2 : 
                              quality === 'good' ? 1.0 : 
                              quality === 'fair' ? 0.85 : 0.7;
    
    const ballSpeed = 30 + Math.random() * 15; // 30-45 m/s
    const batSpeed = 30 + Math.random() * 20; // 30-50 m/s
    
    // Realistic exit velocity calculation using coefficient of restitution
    // Exit velocity should be based on collision physics, not simple multiplication
    const COR = 0.4; // Cricket ball coefficient of restitution
    const baseExitVelocity = (ballSpeed + batSpeed) * COR + batSpeed * 0.3;
    const exitVelocity = Math.min(55, baseExitVelocity * qualityMultiplier); // Cap at 55 m/s
    const launchAngle = 5 + Math.random() * 20; // 5-25 degrees for cricket
    
    // Calculate distance based on realistic cricket physics (including air resistance)
    const g = 9.81;
    const angleRad = (launchAngle * Math.PI) / 180;
    const vacuumDistance = (Math.pow(exitVelocity, 2) * Math.sin(2 * angleRad)) / g;
    
    // Apply realistic factors for cricket ball physics
    const airResistanceFactor = 0.55; // 45% reduction due to drag
    const realismFactor = 0.90; // Real-world conditions
    const baseDistance = vacuumDistance * airResistanceFactor * realismFactor;
    
    const distance = Math.min(
      baseDistance * qualityMultiplier,
      100 // Max 100m for sample data
    );
    
    // Generate timestamp over past 30 days
    const daysAgo = Math.floor(Math.random() * 30);
    const timestamp = new Date(baseDate);
    timestamp.setDate(timestamp.getDate() - daysAgo);
    timestamp.setHours(10 + Math.floor(Math.random() * 8)); // Between 10am and 6pm
    timestamp.setMinutes(Math.floor(Math.random() * 60));
    
    shots.push({
      sessionId: i < 20 ? 'session-001' : `session-${Math.floor(i / 20) + 1}`, // First 20 in session-001
      playerId: `player-${Math.floor(Math.random() * 3) + 1}`, // 3 different players
      tenantId: 'tenant-001',
      timestamp: timestamp.toISOString(),
      shotType,
      quality,
      ballSpeed,
      batSpeed,
      exitVelocity,
      launchAngle,
      horizontalAngle,
      distance,
      height: distance * 0.15 * Math.random() + 2, // Proportional to distance
      spinRate: 1500 + Math.random() * 1500, // 1500-3000 rpm
      impactLocationX: (Math.random() - 0.5) * 0.2, // -0.1 to 0.1
      impactLocationY: Math.random() * 0.3, // 0 to 0.3 (always positive for cricket bat)
      sweetSpotDistance: Math.random() * 0.1, // 0 to 0.1m from sweet spot
      powerTransfer: 0.6 + qualityMultiplier * 0.3, // 0.6 to 0.9+
      timing: quality,
      footwork: quality,
      balance: quality,
      followThrough: quality === 'excellent' || quality === 'good' ? 'complete' : 
                    quality === 'fair' ? 'partial' : 'incomplete'
    });
  }
  
  // Sort by timestamp
  return shots.sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
};

/**
 * Get a smaller set of recent sample shots for quick testing
 */
export const getRecentSampleShots = (count: number = 20): ShotData[] => {
  const allShots = generateSampleShots();
  return allShots.slice(-count); // Return the most recent shots
};

/**
 * Get sample shots for a specific session
 */
export const getSessionSampleShots = (sessionId: string): ShotData[] => {
  const allShots = generateSampleShots();
  return allShots.filter(shot => shot.sessionId === sessionId);
};

/**
 * Pre-generated static sample for consistent testing
 */
export const staticSampleShots: ShotData[] = [
  {
    sessionId: "session-001",
    playerId: "player-001",
    tenantId: "tenant-001",
    timestamp: new Date().toISOString(),
    shotType: "cover-drive",
    quality: "excellent",
    ballSpeed: 38,
    batSpeed: 42,
    exitVelocity: 52,
    launchAngle: 12,
    horizontalAngle: -30,
    distance: 82,
    height: 8,
    spinRate: 2200,
    impactLocationX: 0.01,
    impactLocationY: 0.15,
    sweetSpotDistance: 0.02,
    powerTransfer: 0.88,
    timing: "excellent",
    footwork: "excellent",
    balance: "excellent",
    followThrough: "complete"
  },
  {
    sessionId: "session-001",
    playerId: "player-001",
    tenantId: "tenant-001",
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    shotType: "pull-shot",
    quality: "good",
    ballSpeed: 35,
    batSpeed: 38,
    exitVelocity: 46,
    launchAngle: 18,
    horizontalAngle: 45,
    distance: 68,
    height: 10,
    spinRate: 1800,
    impactLocationX: -0.03,
    impactLocationY: 0.20,
    sweetSpotDistance: 0.04,
    powerTransfer: 0.75,
    timing: "good",
    footwork: "good",
    balance: "good",
    followThrough: "complete"
  },
  {
    sessionId: "session-001",
    playerId: "player-001",
    tenantId: "tenant-001",
    timestamp: new Date(Date.now() - 7200000).toISOString(),
    shotType: "straight-drive",
    quality: "good",
    ballSpeed: 40,
    batSpeed: 40,
    exitVelocity: 50,
    launchAngle: 8,
    horizontalAngle: 0,
    distance: 75,
    height: 5,
    spinRate: 2000,
    impactLocationX: 0,
    impactLocationY: 0.18,
    sweetSpotDistance: 0.03,
    powerTransfer: 0.82,
    timing: "good",
    footwork: "good",
    balance: "good",
    followThrough: "complete"
  },
  {
    sessionId: "session-001",
    playerId: "player-001",
    tenantId: "tenant-001",
    timestamp: new Date(Date.now() - 10800000).toISOString(),
    shotType: "square-cut",
    quality: "fair",
    ballSpeed: 33,
    batSpeed: 35,
    exitVelocity: 40,
    launchAngle: 15,
    horizontalAngle: -75,
    distance: 55,
    height: 7,
    spinRate: 1600,
    impactLocationX: 0.05,
    impactLocationY: 0.10,
    sweetSpotDistance: 0.06,
    powerTransfer: 0.68,
    timing: "fair",
    footwork: "fair",
    balance: "fair",
    followThrough: "partial"
  },
  {
    sessionId: "session-001",
    playerId: "player-001",
    tenantId: "tenant-001",
    timestamp: new Date(Date.now() - 14400000).toISOString(),
    shotType: "flick",
    quality: "excellent",
    ballSpeed: 32,
    batSpeed: 45,
    exitVelocity: 48,
    launchAngle: 20,
    horizontalAngle: 60,
    distance: 70,
    height: 12,
    spinRate: 2500,
    impactLocationX: -0.02,
    impactLocationY: 0.16,
    sweetSpotDistance: 0.025,
    powerTransfer: 0.85,
    timing: "excellent",
    footwork: "excellent",
    balance: "excellent",
    followThrough: "complete"
  }
];

export default generateSampleShots;